import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface MapFiltersProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  selectedPrices: number[];
  onPriceChange: (prices: number[]) => void;
  openNow: boolean;
  onOpenNowChange: (openNow: boolean) => void;
  selectedRadius: number | null;
  onRadiusChange: (radius: number | null) => void;
  onNearMeClick: () => void;
  isLoadingLocation: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

const CATEGORIES = [
  { id: 'all', icon: '🌟', labelEn: 'All', labelMn: 'Бүгд', color: '#0066CC' },
  { id: 'eat', icon: '🍴', labelEn: 'Restaurants', labelMn: 'Ресторан', color: '#FF6B6B' },
  { id: 'party', icon: '🍺', labelEn: 'Nightlife', labelMn: 'Шөнийн клуб', color: '#9B59B6' },
  { id: 'pub', icon: '🍻', labelEn: 'Pubs', labelMn: 'Паб', color: '#E67E22' },
  { id: 'explore', icon: '🏛️', labelEn: 'Attractions', labelMn: 'Үзвэр', color: '#3498DB' },
  { id: 'romantic', icon: '💕', labelEn: 'Date Spots', labelMn: 'Болзоо', color: '#E91E63' },
  { id: 'coffee', icon: '☕', labelEn: 'Cafes', labelMn: 'Кафе', color: '#8B4513' },
  { id: 'unique', icon: '✨', labelEn: 'Hotels', labelMn: 'Зочид буудал', color: '#F39C12' },
  { id: 'shopping', icon: '🛍️', labelEn: 'Shopping', labelMn: 'Дэлгүүр', color: '#E74C3C' },
  { id: 'spa', icon: '💆', labelEn: 'Spa', labelMn: 'Спа', color: '#1ABC9C' },
  { id: 'massage', icon: '💆‍♀️', labelEn: 'Massage', labelMn: 'Массаж', color: '#16A085' },
];

const PRICE_LEVELS = [
  { value: 1, label: '$', description: 'Budget' },
  { value: 2, label: '$$', description: 'Moderate' },
  { value: 3, label: '$$$', description: 'Upscale' },
];

const RADIUS_OPTIONS = [
  { value: 1, label: '1km', color: '#3498DB' },
  { value: 3, label: '3km', color: '#9B59B6' },
  { value: 5, label: '5km', color: '#E91E63' },
];

export default function MapFilters({
  selectedCategory,
  onCategoryChange,
  selectedPrices,
  onPriceChange,
  openNow,
  onOpenNowChange,
  selectedRadius,
  onRadiusChange,
  onNearMeClick,
  isLoadingLocation,
  searchQuery,
  onSearchChange,
}: MapFiltersProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Report height to CSS variable so map can offset correctly
  useEffect(() => {
    const update = () => {
      if (barRef.current) {
        const h = barRef.current.offsetHeight;
        document.documentElement.style.setProperty('--filters-bar-height', `${h}px`);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (barRef.current) ro.observe(barRef.current);
    return () => ro.disconnect();
  }, [showAllCategories]);

  const handleSearch = (val: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange(val), 300);
  };

  const handlePriceToggle = (price: number) => {
    onPriceChange(selectedPrices.includes(price)
      ? selectedPrices.filter(p => p !== price)
      : [...selectedPrices, price]);
  };

  const handleRadiusToggle = (radius: number) => {
    onRadiusChange(selectedRadius === radius ? null : radius);
  };

  const visibleCategories = showAllCategories ? CATEGORIES : CATEGORIES.slice(0, 8);
  const hasActiveFilters = selectedCategory !== 'all' || selectedPrices.length > 0 || openNow || selectedRadius !== null;

  return (
    <div ref={barRef} className="w-full bg-white/97 backdrop-blur-lg border-b border-gray-200 shadow-sm">
      {/* ── Row 1: Search bar ── */}
      <div className="px-3 md:px-5 pt-3 pb-2">
        <div
          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border transition-all duration-200 ${
            isFocused ? 'border-[#0066CC] shadow-[0_0_0_3px_rgba(0,102,204,0.1)]' : 'border-gray-200'
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className={`ri-search-line text-base ${isFocused ? 'text-[#0066CC]' : 'text-gray-400'}`} />
          </div>
          <input
            ref={inputRef}
            type="text"
            defaultValue={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={isEnglish ? 'Search places in Ulaanbaatar…' : 'Улаанбаатарт газар хайх…'}
            className="flex-1 text-sm bg-transparent border-none outline-none placeholder-gray-400 text-gray-900 min-w-0"
          />
          {searchQuery && (
            <button
              onClick={() => { onSearchChange(''); if (inputRef.current) inputRef.current.value = ''; }}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-xs text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* ── Row 2: Category tags (wrap, no scroll) ── */}
      <div className="px-3 md:px-5 pb-2">
        <div className="flex flex-wrap gap-1.5">
          {visibleCategories.map(cat => {
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => onCategoryChange(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isSelected
                    ? 'text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={isSelected ? { backgroundColor: cat.color } : {}}
              >
                <span>{cat.icon}</span>
                <span>{isEnglish ? cat.labelEn : cat.labelMn}</span>
                {isSelected && <i className="ri-check-line text-xs" />}
              </button>
            );
          })}

          {CATEGORIES.length > 8 && (
            <button
              onClick={() => setShowAllCategories(v => !v)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
            >
              <span>{showAllCategories ? (isEnglish ? 'Less' : 'Хураах') : (isEnglish ? 'More' : 'Цааш')}</span>
              <i className={`ri-arrow-${showAllCategories ? 'up' : 'down'}-s-line text-xs`} />
            </button>
          )}
        </div>
      </div>

      {/* ── Row 3: Extra filters ── */}
      <div className="px-3 md:px-5 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Price */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-xl border border-gray-100">
            <i className="ri-money-dollar-circle-line text-sm text-gray-500" />
            <span className="text-xs font-medium text-gray-600">{isEnglish ? 'Price:' : 'Үнэ:'}</span>
            {PRICE_LEVELS.map(price => (
              <button
                key={price.value}
                onClick={() => handlePriceToggle(price.value)}
                title={price.description}
                className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedPrices.includes(price.value)
                    ? 'bg-[#0066CC] text-white shadow-sm'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {price.label}
              </button>
            ))}
          </div>

          {/* Open Now */}
          <button
            onClick={() => onOpenNowChange(!openNow)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              openNow ? 'bg-[#4CAF50] text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-100'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${openNow ? 'bg-white animate-pulse' : 'bg-gray-400'}`} />
            <span>{isEnglish ? 'Open Now' : 'Одоо нээлттэй'}</span>
          </button>

          {/* Near Me */}
          <button
            onClick={onNearMeClick}
            disabled={isLoadingLocation}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              isLoadingLocation
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#0066CC] text-white hover:bg-[#0052A3] shadow-sm hover:shadow-md'
            }`}
          >
            {isLoadingLocation ? (
              <>
                <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>{isEnglish ? 'Locating…' : 'Байршил тогтоож байна…'}</span>
              </>
            ) : (
              <>
                <i className="ri-map-pin-user-line text-sm" />
                <span>{isEnglish ? 'Near Me' : 'Ойролцоо'}</span>
              </>
            )}
          </button>

          {/* Radius (only when active) */}
          {selectedRadius !== null && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-xl border border-gray-100">
              <i className="ri-radar-line text-sm text-gray-500" />
              <span className="text-xs font-medium text-gray-600">{isEnglish ? 'Radius:' : 'Радиус:'}</span>
              {RADIUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleRadiusToggle(opt.value)}
                  className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    selectedRadius === opt.value
                      ? 'text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                  style={selectedRadius === opt.value ? { backgroundColor: opt.color } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Clear All */}
          {hasActiveFilters && (
            <button
              onClick={() => { onCategoryChange('all'); onPriceChange([]); onOpenNowChange(false); onRadiusChange(null); }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium bg-red-50 text-red-500 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap border border-red-100"
            >
              <i className="ri-close-circle-line text-sm" />
              <span>{isEnglish ? 'Clear All' : 'Бүгдийг арилгах'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
